import { StickerCard } from "@/components/sticker-card/StickerCard.tsx";
import {
  PerformanceReviewEditPageProps,
  usePerformanceReviewEditPage,
} from "@/pages/talent/performance-reviews/edit/pages/performance-review-edit-pages.tsx";
import {
  usePerformanceReviewDetails,
  usePerformanceReviewLaunch,
} from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiPerformanceReviewStatus,
  getApiPerformanceReviewStatusTitle,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { Button, FullScreenPage, Stack, Text, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export const PerformanceReviewEditFinalPage = ({ review }: PerformanceReviewEditPageProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { exit } = usePerformanceReviewEditPage();

  const { data: reviewData } = usePerformanceReviewDetails(review.id);

  const launchReview = usePerformanceReviewLaunch();

  const isAbleToLaunchReviewInstant = useMemo(() => {
    return review.date_starts_at == null || review.date_starts_at <= new Date();
  }, [review]);

  const handleLaunchReview = async () => {
    try {
      await launchReview.mutateAsync({ id: review.id });

      analyticsService.trackEvent(analyticEvents.performanceReviews.launched, {
        [analyticProperties.id]: review.id,
      });

      navigate(`/performance-reviews`);
    } catch {}
  };

  function getTitleComponent() {
    if (review.status !== ApiPerformanceReviewStatus.draft) {
      return (
        <Title
          size={"lg"}
          header={t("performance_review:review_has_been_updated_title")}
          caption={t("performance_review:review_has_been_updated_description")}
        />
      );
    }

    if (isAbleToLaunchReviewInstant) {
      return (
        <Title
          size={"lg"}
          header={t("performance_review:all_set_and_ready_title")}
          caption={t("performance_review:all_set_and_ready_description")}
        />
      );
    } else {
      return (
        <Title
          size={"lg"}
          header={t("performance_review:all_set_and_ready_schedule_title")}
          caption={t("performance_review:all_set_and_ready_schedule_description")}
        />
      );
    }
  }

  function getCTAButtons() {
    if (review.status !== ApiPerformanceReviewStatus.draft) {
      return (
        <Button size={"lg"} variant={"primary"} onClick={() => exit(review.id)}>
          {t("performance_review:go_to_review")}
        </Button>
      );
    }

    return (
      <>
        <Button size={"lg"} variant={"primary"} onClick={handleLaunchReview}>
          {isAbleToLaunchReviewInstant
            ? t("performance_review:start_review")
            : t("performance_review:launch_on_schedule")}
        </Button>

        <Button size={"lg"} variant={"secondary"} onClick={() => exit(review.id)}>
          {t("performance_review:skip_and_leave_as_draft")}
        </Button>
      </>
    );
  }

  return (
    <FullScreenPage isCenterVertical={true} contentSize={"lg"}>
      <Stack direction={"horizontal"} items={"center"}>
        <StickerCard
          title={review.title}
          caption={review.description || undefined}
          emoji={review.emoji || undefined}
          status={getApiPerformanceReviewStatusTitle(review.status)}
          contentSlot={
            reviewData && (
              <>
                <Text className={"text-secondary"}>
                  {reviewData.reviewer_types
                    .map((reviewer_type) => {
                      return (
                        PerformanceReviewReviewerTypeItems().find(
                          (item) => item.id == reviewer_type.type
                        )?.shortTitle ??
                        reviewer_type.title ??
                        ""
                      );
                    })
                    .join(", ")}
                </Text>
                <Text className={"text-secondary"}>
                  {t("plurals:people_total", { count: reviewData.reviewer_stats.total_count })}
                </Text>
              </>
            )
          }
        />
        <Stack className={"pl-18 pr-1"} gap={"lg"}>
          {getTitleComponent()}
          <Stack direction={"horizontal"} gap={"sm"}>
            {getCTAButtons()}
          </Stack>
        </Stack>
      </Stack>
    </FullScreenPage>
  );
};
